import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconDownload,
  IconFlame,
  IconSearch,
  IconTicket,
} from '@tabler/icons-react';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';

const COMPANY_NAME = 'Cencosud';

const normalizarCodigo = (value = '') => String(value ?? '')
  .trim()
  .replace(/\s+/g, '')
  .toUpperCase();

const formatearMonto = (value = 0) => Number(value || 0).toLocaleString('es-CL');

const formatearFecha = (value) => {
  if (!value) return 'Sin fecha';
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin fecha';
  return date.toLocaleString('es-CL', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const descargarCsv = (rows) => {
  const headers = [
    'Codigo',
    'Empresa',
    'Tipo',
    'Fecha uso',
    'Usuario interno',
    'Cliente',
    'Monto usado',
    'Descuento aplicado (%)',
    'Servicio o producto',
    'Observacion',
  ];

  const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const csvRows = rows.map((item) => [
    item.codeNormalized || item.id,
    item.companyName || COMPANY_NAME,
    item.codeType || '',
    formatearFecha(item.usedAt),
    item.usedBy || '',
    item.customerName || '',
    Number(item.amountUsed || 0),
    Number(item.discountPercent ?? item.discountAmount ?? 0),
    item.serviceDescription || '',
    item.observation || '',
  ].map(escapeCsv).join(','));

  const blob = new Blob([[headers.join(','), ...csvRows].join('\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `codigos_convenio_cencosud_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  window.URL.revokeObjectURL(url);
};

const getStatusBadge = (status) => (
  status === 'used'
    ? { label: 'Usado', color: 'gray' }
    : { label: 'Disponible', color: 'green' }
);

const CodigosConvenio = () => {
  const { currentUser } = useAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [typeFilter, setTypeFilter] = useState('todos');
  const [selectedCode, setSelectedCode] = useState(null);
  const [useModalOpen, setUseModalOpen] = useState(false);
  const [savingUse, setSavingUse] = useState(false);

  const form = useForm({
    initialValues: {
      customerName: '',
      amountUsed: 0,
      discountPercent: 0,
      serviceDescription: '',
      observation: '',
    },
    validate: {
      customerName: (value) => (value.trim() ? null : 'Ingresa el nombre de la persona'),
      amountUsed: (value) => (Number(value) > 0 ? null : 'Ingresa un monto usado mayor a 0'),
      discountPercent: (value) => {
        const percent = Number(value);
        if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
          return 'El descuento debe estar entre 0% y 100%';
        }
        return null;
      },
      serviceDescription: (value) => (value.trim() ? null : 'Ingresa el servicio o producto atendido'),
    },
  });

  useEffect(() => {
    const codesQuery = query(collection(db, 'convenioCodes'), orderBy('codeNormalized', 'asc'));

    const unsubscribe = onSnapshot(
      codesQuery,
      (snapshot) => {
        setCodes(snapshot.docs.map((codeDoc) => ({
          id: codeDoc.id,
          ...codeDoc.data(),
        })));
        setLoading(false);
        setError('');
      },
      (snapshotError) => {
        console.error('Error al cargar códigos de convenio:', snapshotError);
        setError('No se pudieron cargar los códigos de convenio.');
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const stats = useMemo(() => {
    const usedCodes = codes.filter((item) => item.status === 'used');
    return {
      available: codes.filter((item) => item.status !== 'used').length,
      used: usedCodes.length,
      amountUsed: usedCodes.reduce((total, item) => total + Number(item.amountUsed || 0), 0),
      averageDiscount: usedCodes.length
        ? usedCodes.reduce((total, item) => total + Number(item.discountPercent ?? item.discountAmount ?? 0), 0) / usedCodes.length
        : 0,
    };
  }, [codes]);

  const filteredCodes = useMemo(() => {
    const normalizedSearch = normalizarCodigo(search);

    return codes.filter((item) => {
      const code = item.codeNormalized || item.id || '';
      const matchesSearch = !normalizedSearch || code.includes(normalizedSearch);
      const matchesStatus = statusFilter === 'todos' || (item.status || 'available') === statusFilter;
      const matchesType = typeFilter === 'todos' || item.codeType === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [codes, search, statusFilter, typeFilter]);

  const usedCodes = useMemo(
    () => codes
      .filter((item) => item.status === 'used')
      .sort((a, b) => {
        const dateA = typeof a.usedAt?.toDate === 'function' ? a.usedAt.toDate().getTime() : new Date(a.usedAt || 0).getTime();
        const dateB = typeof b.usedAt?.toDate === 'function' ? b.usedAt.toDate().getTime() : new Date(b.usedAt || 0).getTime();
        return dateB - dateA;
      }),
    [codes]
  );

  const openUseModal = (code) => {
    if (code.status === 'used') {
      notifications.show({
        title: 'Código ya usado',
        message: 'Este código ya fue quemado y no puede usarse nuevamente.',
        color: 'yellow',
      });
      return;
    }

    setSelectedCode(code);
    form.setValues({
      customerName: '',
      amountUsed: 0,
      discountPercent: 0,
      serviceDescription: '',
      observation: '',
    });
    form.resetDirty();
    setUseModalOpen(true);
  };

  const handleUseCode = async (values) => {
    if (!selectedCode) return;
    const validation = form.validate();
    if (validation.hasErrors) return;

    try {
      setSavingUse(true);
      const codeRef = doc(db, 'convenioCodes', selectedCode.id);

      await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(codeRef);
        if (!snapshot.exists()) {
          throw new Error('El código ya no existe.');
        }

        const currentData = snapshot.data();
        if (currentData.status === 'used') {
          throw new Error('Este código ya fue usado.');
        }

        transaction.update(codeRef, {
          status: 'used',
          usedAt: serverTimestamp(),
          usedBy: currentUser?.email || '',
          usedByUid: currentUser?.uid || '',
          customerName: values.customerName.trim(),
          amountUsed: Number(values.amountUsed || 0),
          discountPercent: Number(values.discountPercent || 0),
          serviceDescription: values.serviceDescription.trim(),
          observation: values.observation.trim(),
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.email || '',
        });
      });

      notifications.show({
        title: 'Código quemado',
        message: `${selectedCode.codeNormalized || selectedCode.id} quedó registrado para cobranza.`,
        color: 'green',
      });
      setUseModalOpen(false);
      setSelectedCode(null);
    } catch (useError) {
      console.error('Error al quemar código de convenio:', useError);
      notifications.show({
        title: 'No se pudo quemar el código',
        message: useError.message || 'Intenta nuevamente.',
        color: 'red',
      });
    } finally {
      setSavingUse(false);
    }
  };

  return (
    <Container size="xl" py="md">
      <Paper shadow="sm" p={{ base: 'md', md: 'xl' }} radius="xl" withBorder className="surface-panel">
        <Group justify="space-between" align="flex-start" mb="xl" gap="md">
          <Stack gap={6} style={{ flex: 1, minWidth: 260 }}>
            <Badge color="teal" variant="light" w="fit-content">
              Convenios empresa
            </Badge>
            <Title order={2} style={{ letterSpacing: '-0.03em' }}>
              Códigos de convenio
            </Title>
            <Text c="dimmed" maw={760}>
              Administra códigos Cencosud de uso único, registra atenciones y deja respaldo para cobranza.
            </Text>
          </Stack>
          <Group gap="xs">
            <Button
              variant="light"
              color="teal"
              leftSection={<IconDownload size={18} />}
              onClick={() => descargarCsv(usedCodes)}
              disabled={usedCodes.length === 0}
            >
              Exportar usados
            </Button>
          </Group>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md" mb="xl">
          <Card withBorder>
            <Text size="sm" c="dimmed" fw={700}>Disponibles</Text>
            <Title order={3}>{stats.available}</Title>
          </Card>
          <Card withBorder>
            <Text size="sm" c="dimmed" fw={700}>Usados</Text>
            <Title order={3}>{stats.used}</Title>
          </Card>
          <Card withBorder>
            <Text size="sm" c="dimmed" fw={700}>Monto usado</Text>
            <Title order={3}>$ {formatearMonto(stats.amountUsed)}</Title>
          </Card>
          <Card withBorder>
            <Text size="sm" c="dimmed" fw={700}>Descuento promedio</Text>
            <Title order={3}>{Number(stats.averageDiscount || 0).toLocaleString('es-CL', { maximumFractionDigits: 1 })}%</Title>
          </Card>
        </SimpleGrid>

        <Card withBorder radius="lg" p={{ base: 'md', md: 'lg' }} mb="lg" bg="white">
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <TextInput
              label="Buscar código"
              placeholder="Ej: S-CLUBO-7K9M4X2Q8R"
              value={search}
              onChange={(event) => setSearch(normalizarCodigo(event.currentTarget.value))}
              leftSection={<IconSearch size={18} />}
            />
            <Select
              label="Estado"
              value={statusFilter}
              onChange={(value) => setStatusFilter(value || 'todos')}
              data={[
                { value: 'todos', label: 'Todos' },
                { value: 'available', label: 'Disponibles' },
                { value: 'used', label: 'Usados' },
              ]}
            />
            <Select
              label="Tipo"
              value={typeFilter}
              onChange={(value) => setTypeFilter(value || 'todos')}
              data={[
                { value: 'todos', label: 'Todos' },
                { value: 'servicio', label: 'Servicios' },
                { value: 'producto', label: 'Productos' },
              ]}
            />
          </SimpleGrid>
        </Card>

        {error && (
          <Alert icon={<IconAlertCircle size={18} />} color="red" mb="lg">
            {error}
          </Alert>
        )}

        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
            <Text c="dimmed">Cargando códigos...</Text>
          </Group>
        ) : (
          <Table.ScrollContainer minWidth={980}>
            <Table verticalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Código</Table.Th>
                  <Table.Th>Empresa</Table.Th>
                  <Table.Th>Tipo</Table.Th>
                  <Table.Th>Estado</Table.Th>
                  <Table.Th>Uso / cliente</Table.Th>
                  <Table.Th>Monto</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Acciones</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredCodes.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Text ta="center" c="dimmed" py="xl">No hay códigos para estos filtros.</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : filteredCodes.map((item) => {
                  const status = getStatusBadge(item.status);
                  const isUsed = item.status === 'used';

                  return (
                    <Table.Tr key={item.id}>
                      <Table.Td>
                        <Text fw={900} ff="monospace">{item.codeNormalized || item.id}</Text>
                      </Table.Td>
                      <Table.Td>{item.companyName || COMPANY_NAME}</Table.Td>
                      <Table.Td>
                        <Badge color={item.codeType === 'producto' ? 'violet' : 'teal'} variant="light">
                          {item.codeType === 'producto' ? 'Producto' : 'Servicio'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={status.color} variant="light">{status.label}</Badge>
                      </Table.Td>
                      <Table.Td>
                        {isUsed ? (
                          <Stack gap={2}>
                            <Text fw={700}>{item.customerName || 'Cliente no registrado'}</Text>
                            <Text size="xs" c="dimmed">{formatearFecha(item.usedAt)} · {item.usedBy || 'Sin usuario'}</Text>
                            <Text size="xs" c="dimmed">{item.serviceDescription || 'Sin detalle'}</Text>
                          </Stack>
                        ) : (
                          <Text c="dimmed" size="sm">Pendiente de uso</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {isUsed ? (
                          <Stack gap={2}>
                            <Text fw={800}>$ {formatearMonto(item.amountUsed)}</Text>
                            <Text size="xs" c="dimmed">Desc: {Number(item.discountPercent ?? item.discountAmount ?? 0).toLocaleString('es-CL', { maximumFractionDigits: 1 })}%</Text>
                          </Stack>
                        ) : (
                          <Text c="dimmed" size="sm">-</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group justify="flex-end">
                          <Tooltip label={isUsed ? 'Código ya usado' : 'Quemar código'}>
                            <ActionIcon
                              color="teal"
                              variant={isUsed ? 'subtle' : 'light'}
                              disabled={isUsed}
                              onClick={() => openUseModal(item)}
                              aria-label="Quemar código"
                            >
                              <IconFlame size={18} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Paper>

      <Modal
        opened={useModalOpen}
        onClose={() => setUseModalOpen(false)}
        title={`Quemar código ${selectedCode?.codeNormalized || ''}`}
        size="lg"
      >
        <form onSubmit={form.onSubmit(handleUseCode)}>
          <Stack>
            <Alert icon={<IconTicket size={18} />} color="teal" variant="light">
              Este código quedará marcado como usado y no podrá quemarse nuevamente.
            </Alert>
            <TextInput
              label="Nombre de la persona"
              placeholder="Cliente que usó el código"
              {...form.getInputProps('customerName')}
            />
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <NumberInput
                label="Monto usado"
                min={1}
                thousandSeparator="."
                decimalSeparator=","
                prefix="$ "
                {...form.getInputProps('amountUsed')}
              />
              <NumberInput
                label="Descuento aplicado"
                description="Sólo informativo. No modifica el monto usado."
                min={0}
                max={100}
                decimalSeparator=","
                suffix="%"
                {...form.getInputProps('discountPercent')}
              />
            </SimpleGrid>
            <TextInput
              label="Servicio o producto atendido"
              placeholder="Ej: corte + barba"
              {...form.getInputProps('serviceDescription')}
            />
            <Textarea
              label="Observación"
              placeholder="Detalle opcional para cobranza"
              minRows={3}
              {...form.getInputProps('observation')}
            />
            <Group justify="flex-end" mt="md">
              <Button variant="light" color="gray" onClick={() => setUseModalOpen(false)} type="button">
                Cancelar
              </Button>
              <Button leftSection={<IconFlame size={16} />} loading={savingUse} type="submit">
                Quemar código
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Container>
  );
};

export default CodigosConvenio;
